import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Clock, 
  BookOpen,
  TrendingUp,
  Loader2
} from "lucide-react";

const CATEGORY_COLORS = {
  marketing: "bg-primary/20 text-primary border-primary/30",
  business: "bg-secondary/20 text-secondary border-secondary/30",
  income: "bg-green-500/20 text-green-400 border-green-500/30",
  branding: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  industry: "bg-blue-500/20 text-blue-400 border-blue-500/30"
};

export default function BlogWidget({ limit = 3, showHeader = true }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API}/blog/posts?limit=${limit}`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error("Failed to fetch blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  const featuredPost = posts.find(p => p.featured) || posts[0];
  const otherPosts = posts.filter(p => p.post_id !== featuredPost.post_id).slice(0, 2);

  return (
    <section className="py-16 lg:py-24" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-6">
        {showHeader && (
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 bg-secondary/10 border border-secondary/20 rounded-full px-4 py-2 text-sm text-secondary mb-4"
              >
                <BookOpen className="w-4 h-4" />
                Artist Resources
              </motion.div>
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold"
              >
                Level Up Your <span className="text-primary">Career</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-muted-foreground text-lg mt-3 max-w-xl"
              >
                Tips, strategies, and industry insights for independent artists
              </motion.p>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/blog">
                <Button variant="outline" className="rounded-full mt-4 sm:mt-0">
                  View All Articles
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Featured Post */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="lg:row-span-2"
          >
            <Card className="h-full bg-card border-border overflow-hidden group hover:border-primary/50 transition-all">
              <div className="relative h-64 lg:h-80 overflow-hidden">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                <div className="absolute top-4 left-4">
                  <Badge className={`${CATEGORY_COLORS[featuredPost.category]} border`}>
                    {featuredPost.category}
                  </Badge>
                </div>
                {featuredPost.featured && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-primary text-primary-foreground">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Featured
                    </Badge>
                  </div>
                )}
              </div>
              <CardContent className="p-6">
                <h3 className="font-heading text-xl lg:text-2xl font-bold mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                  {featuredPost.title}
                </h3>
                <p className="text-muted-foreground mb-4 line-clamp-3">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{featuredPost.author}</span>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {featuredPost.read_time}
                    </span>
                    <span>{formatDate(featuredPost.published_at)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Other Posts */}
          <div className="space-y-6">
            {otherPosts.map((post, idx) => (
              <motion.div
                key={post.post_id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + idx * 0.1 }}
              >
                <Card className="bg-card border-border overflow-hidden group hover:border-primary/50 transition-all">
                  <div className="flex flex-col sm:flex-row">
                    <div className="relative w-full sm:w-48 h-40 sm:h-auto flex-shrink-0 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-2 left-2 sm:hidden">
                        <Badge className={`${CATEGORY_COLORS[post.category]} border text-xs`}>
                          {post.category}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-4 sm:p-5 flex flex-col justify-center">
                      <div className="hidden sm:block mb-2">
                        <Badge className={`${CATEGORY_COLORS[post.category]} border text-xs`}>
                          {post.category}
                        </Badge>
                      </div>
                      <h3 className="font-heading font-bold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2 hidden sm:block">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.read_time}
                        </span>
                        <span>{formatDate(post.published_at)}</span>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
